import React from 'react';
import { Pie, Doughnut, Bar, Line } from 'react-chartjs-2';
import { ChartOptions, ChartType } from 'chart.js';
import { MarketData } from '../utils/dataExtractor';
import { getCommonChartOptions, chartColors } from '../../../config/chartConfig';

interface MarketAnalysisChartsProps {
  data: MarketData;
}

const MarketAnalysisCharts: React.FC<MarketAnalysisChartsProps> = ({ data }) => {
  // Get common chart options from centralized configuration
  const baseOptions = getCommonChartOptions('bar');

  return (
    <div className="market-analysis-charts">
      <div className="chart-row">
        <div className="chart-col">
          <div className="chart-container">
            <div className="chart-title">Market Share</div>
            <Doughnut
              type="doughnut"
              options={{
                ...baseOptions,
                plugins: {
                  ...baseOptions.plugins,
                  legend: {
                    ...baseOptions.plugins?.legend,
                    position: 'right' as const,
                  },
                },
              }}
              data={{
                labels: data.marketShare.labels,
                datasets: [
                  {
                    label: 'Market Share (%)',
                    data: data.marketShare.values,
                    backgroundColor: [
                      'rgba(59, 130, 246, 0.7)',
                      'rgba(16, 185, 129, 0.7)',
                      'rgba(245, 158, 11, 0.7)',
                      'rgba(99, 102, 241, 0.7)',
                      'rgba(236, 72, 153, 0.7)',
                    ],
                    borderColor: [
                      'rgba(59, 130, 246, 1)',
                      'rgba(16, 185, 129, 1)',
                      'rgba(245, 158, 11, 1)',
                      'rgba(99, 102, 241, 1)',
                      'rgba(236, 72, 153, 1)',
                    ],
                    borderWidth: 1,
                  },
                ],
              }}
            />
          </div>
        </div>
        <div className="chart-col">
          <div className="chart-container">
            <div className="chart-title">Target Market Segments</div>
            <Pie
              type="pie"
              options={{
                ...baseOptions,
                plugins: {
                  ...baseOptions.plugins,
                  legend: {
                    ...baseOptions.plugins?.legend,
                    position: 'right' as const,
                  },
                },
              }}
              data={{
                labels: data.marketSegments.labels,
                datasets: [
                  {
                    label: 'Market Segments (%)',
                    data: data.marketSegments.values,
                    backgroundColor: [
                      'rgba(16, 185, 129, 0.7)',
                      'rgba(59, 130, 246, 0.7)',
                      'rgba(245, 158, 11, 0.7)',
                      'rgba(99, 102, 241, 0.7)',
                    ],
                    borderColor: [
                      'rgba(16, 185, 129, 1)',
                      'rgba(59, 130, 246, 1)',
                      'rgba(245, 158, 11, 1)',
                      'rgba(99, 102, 241, 1)',
                    ],
                    borderWidth: 1,
                  },
                ],
              }}
            />
          </div>
        </div>
      </div>
      <div className="chart-row">
        <div className="chart-col">
          <div className="chart-container">
            <div className="chart-title">SWOT Analysis</div>
            <Bar
              type="bar"
              options={{
                ...baseOptions,
                plugins: {
                  ...baseOptions.plugins,
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    max: 10,
                    ticks: {
                      stepSize: 2
                    }
                  }
                }
              }}
              data={{
                labels: data.swotChart.labels,
                datasets: [
                  {
                    label: 'SWOT Analysis',
                    data: data.swotChart.values,
                    backgroundColor: [
                      'rgba(16, 185, 129, 0.7)',  // Strengths
                      'rgba(239, 68, 68, 0.7)',   // Weaknesses
                      'rgba(59, 130, 246, 0.7)',  // Opportunities
                      'rgba(245, 158, 11, 0.7)',  // Threats
                    ],
                    borderColor: [
                      'rgba(16, 185, 129, 1)',
                      'rgba(239, 68, 68, 1)',
                      'rgba(59, 130, 246, 1)',
                      'rgba(245, 158, 11, 1)',
                    ],
                    borderWidth: 1,
                  },
                ],
              }}
            />
          </div>
        </div>
        <div className="chart-col">
          <div className="chart-container">
            <div className="chart-title">Market Growth Trend</div>
            <Line
              type="line"
              options={baseOptions}
              data={{
                labels: data.growthTrend.labels,
                datasets: [
                  {
                    label: 'Market Growth (%)',
                    data: data.growthTrend.values,
                    borderColor: 'rgba(16, 185, 129, 1)',
                    backgroundColor: 'rgba(16, 185, 129, 0.2)',
                    borderWidth: 2,
                    fill: true,
                    tension: 0.1,
                  },
                ],
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketAnalysisCharts;
